/**
 * Lazy Loading Utilities
 *
 * Utilities for implementing lazy loading with fallback UI and error boundaries
 * to improve bundle size and performance.
 */

import React, { Suspense, lazy } from "react";

/**
 * Generic Loading Component
 */
export const LoadingFallback = ({
  message = "Loading...",
  size = "medium",
}) => {
  const sizeClasses = {
    small: "h-32",
    medium: "h-64",
    large: "h-96",
    fullscreen: "h-screen",
  };

  return (
    <div
      className={`flex items-center justify-center ${sizeClasses[size]} bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900`}
    >
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-400 mx-auto mb-4"></div>
        <p className="text-gray-300 text-lg">{message}</p>
      </div>
    </div>
  );
};

/**
 * Error Boundary for Lazy Components
 */
export class LazyLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Lazy loading error:", error, errorInfo);

    // Report to error monitoring service
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="flex items-center justify-center h-64 bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900">
            <div className="text-center">
              <div className="text-red-400 text-xl mb-4">⚠️ Loading Error</div>
              <p className="text-gray-300">Failed to load component</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-violet-600 text-white rounded hover:bg-violet-700 transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

/**
 * HOC for lazy loading with custom loading and error handling
 */
export const withLazyLoading = (
  importFn,
  loadingComponent = <LoadingFallback />,
  errorFallback = null,
) => {
  const LazyComponent = lazy(importFn);

  return (props) => (
    <LazyLoadErrorBoundary fallback={errorFallback}>
      <Suspense fallback={loadingComponent}>
        <LazyComponent {...props} />
      </Suspense>
    </LazyLoadErrorBoundary>
  );
};

/**
 * Progressive loading utilities
 */
export class ProgressiveLoader {
  constructor() {
    this.loadingQueue = [];
    this.loaded = new Set();
  }

  // Add component to loading queue
  addToQueue(componentName, importFn, priority = 1) {
    if (this.loaded.has(componentName)) return;

    this.loadingQueue.push({
      name: componentName,
      import: importFn,
      priority,
    });

    // Sort by priority (higher priority first)
    this.loadingQueue.sort((a, b) => b.priority - a.priority);
  }

  // Load next component in queue
  async loadNext() {
    if (this.loadingQueue.length === 0) return null;

    const next = this.loadingQueue.shift();

    try {
      await next.import();
      this.loaded.add(next.name);
      return next.name;
    } catch (error) {
      console.error(`Failed to preload ${next.name}:`, error);
      return null;
    }
  }

  // Load all queued components
  async loadAll() {
    const results = [];
    while (this.loadingQueue.length > 0) {
      const loaded = await this.loadNext();
      if (loaded) results.push(loaded);
    }
    return results;
  }
}

// Global progressive loader instance
export const progressiveLoader = new ProgressiveLoader();
